import { useState, useEffect, useCallback } from "react";
import EmptyState from "./EmptyState";

const API      = process.env.REACT_APP_API_URL || "http://localhost:4000/api";
const getToken = () => localStorage.getItem("sellsync_token");
const api = async (path, opts = {}) => {
  const res = await fetch(API + path, {
    ...opts,
    headers: { "Content-Type":"application/json", Authorization:"Bearer "+getToken(), ...opts.headers },
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || "Request failed");
  return data;
};

const S = {
  card:    { background:"#fff", borderRadius:10, border:"1px solid #e5e7eb", marginBottom:14 },
  cardHdr: { padding:"11px 16px", borderBottom:"1px solid #e5e7eb", display:"flex", alignItems:"center", justifyContent:"space-between", fontWeight:600, fontSize:13 },
  btn:     (c="#F47920") => ({ padding:"8px 16px", borderRadius:7, fontSize:12, cursor:"pointer", background:c, color:"#fff", border:"none", fontFamily:"inherit", fontWeight:600 }),
  btnOut:  { padding:"7px 14px", borderRadius:7, fontSize:12, cursor:"pointer", background:"#fff", color:"#374151", border:"1px solid #d1d5db", fontFamily:"inherit" },
  input:   { width:"100%", padding:"8px 10px", borderRadius:7, border:"1px solid #d1d5db", fontSize:13, fontFamily:"inherit", outline:"none", boxSizing:"border-box" },
  label:   { fontSize:11, color:"#6b7280", fontWeight:600, display:"block", marginBottom:3 },
  tab:     (a) => ({ padding:"10px 20px", background:"none", border:"none", borderBottom:a?"2px solid #F47920":"2px solid transparent", color:a?"#F47920":"#6b7280", fontWeight:a?600:400, fontSize:13, cursor:"pointer", fontFamily:"inherit" }),
  toggle:  (on) => ({ width:44, height:24, borderRadius:12, background:on?"#22c55e":"#d1d5db", position:"relative", cursor:"pointer", transition:"background .2s", flexShrink:0, border:"none" }),
  knob:    (on) => ({ width:20, height:20, borderRadius:"50%", background:"#fff", position:"absolute", top:2, left:on?22:2, transition:"left .2s", boxShadow:"0 1px 3px rgba(0,0,0,0.2)" }),
};

const TRIGGER_LABELS = {
  order_created:  { label:"New Order",           icon:"🛍️", color:"#2563eb" },
  order_paid:     { label:"Payment Confirmed",   icon:"💰", color:"#15803d" },
  inventory_low:  { label:"Low Stock",           icon:"⚠️", color:"#d97706" },
  inventory_out:  { label:"Out of Stock",        icon:"❌", color:"#dc2626" },
  return_created: { label:"Return Initiated",    icon:"↩️", color:"#7c3aed" },
  shipment_sent:  { label:"Shipment Dispatched", icon:"🚚", color:"#0ea5e9" },
  daily:          { label:"Every Day (8 AM)",    icon:"📅", color:"#6366f1" },
  manual:         { label:"Manual Only",         icon:"👆", color:"#6b7280" },
};

const ACTION_LABELS = {
  update_inventory: { label:"Update Inventory", icon:"📦", color:"#5a8a00" },
  send_whatsapp:    { label:"Send WhatsApp",     icon:"💬", color:"#25D366" },
  send_email:       { label:"Send Email",        icon:"📧", color:"#2563eb" },
  webhook:          { label:"Call Webhook",      icon:"🔗", color:"#6366f1" },
};

const TEMPLATES = [
  { name:"📦 Auto-update inventory on order", description:"When a Shopify order arrives, automatically reduce stock for each item ordered.", trigger:{ type:"order_created", platform:"shopify" }, conditions:[], actions:[{ type:"update_inventory", params:{ reduction_type:"exact" } }], color:"#5a8a00", popular:true },
  { name:"💬 WhatsApp alert — new order", description:"Get a WhatsApp message instantly when any new order arrives on any platform.", trigger:{ type:"order_created", platform:"all" }, conditions:[], actions:[{ type:"send_whatsapp", params:{ phone:"", message:"🛍️ New Order!\n\nOrder: {{order_id}}\nCustomer: {{customer}}\nTotal: {{total}}\nItems: {{items}}" } }], color:"#25D366", popular:true },
  { name:"⚠️ Low stock WhatsApp alert", description:"Get notified on WhatsApp when any product stock falls below 10 units.", trigger:{ type:"inventory_low", platform:"all" }, conditions:[{ field:"product.stock", operator:"lte", value:"10" }], actions:[{ type:"send_whatsapp", params:{ phone:"", message:"⚠️ Low Stock!\n\nProduct: {{product}}\nStock: {{stock}} units" } }], color:"#d97706", popular:true },
  { name:"🎉 High value order alert (₹5,000+)", description:"Send WhatsApp for orders above ₹5,000.", trigger:{ type:"order_created", platform:"all" }, conditions:[{ field:"order.total_price", operator:"gt", value:"5000" }], actions:[{ type:"send_whatsapp", params:{ phone:"", message:"🎉 High Value Order!\n\nOrder: {{order_id}}\nTotal: {{total}}" } }], color:"#7c3aed", popular:false },
  { name:"💰 Payment confirmed → update stock", description:"Reduce inventory only when payment is confirmed.", trigger:{ type:"order_paid", platform:"shopify" }, conditions:[], actions:[{ type:"update_inventory", params:{ reduction_type:"exact" } }], color:"#15803d", popular:false },
  { name:"🔗 Webhook on paid order", description:"Notify your ERP or Tally via webhook when payment is confirmed.", trigger:{ type:"order_paid", platform:"all" }, conditions:[], actions:[{ type:"webhook", params:{ url:"https://your-erp.com/webhook", method:"POST" } }], color:"#2563eb", popular:false },
  { name:"↩️ Return initiated alert", description:"Get WhatsApp notification when a customer raises a return.", trigger:{ type:"return_created", platform:"all" }, conditions:[], actions:[{ type:"send_whatsapp", params:{ phone:"", message:"↩️ Return Request!\n\nOrder: {{order_id}}\nCustomer: {{customer}}" } }], color:"#dc2626", popular:false },
  { name:"🚚 Shipment dispatched notification", description:"Send WhatsApp when an order is dispatched.", trigger:{ type:"shipment_sent", platform:"all" }, conditions:[], actions:[{ type:"send_whatsapp", params:{ phone:"", message:"🚚 Your order is on the way!\n\nOrder: {{order_id}}" } }], color:"#0ea5e9", popular:false },
];

function QuickEditModal({ auto, onSave, onClose, toast }) {
  const [form, setForm] = useState({ ...auto });
  const action = form.actions?.[0];
  const set = (key, val) => {
    const newActions = [...(form.actions||[])];
    if (newActions[0]) newActions[0] = { ...newActions[0], params: { ...newActions[0].params, [key]: val } };
    setForm((f) => ({ ...f, actions: newActions }));
  };
  return (
    <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.5)", display:"flex", alignItems:"center", justifyContent:"center", zIndex:1000, padding:16 }}>
      <div style={{ background:"#fff", borderRadius:12, width:"100%", maxWidth:460, boxShadow:"0 20px 60px rgba(0,0,0,0.25)", overflow:"hidden" }}>
        <div style={{ padding:"14px 18px", borderBottom:"1px solid #e5e7eb", display:"flex", justifyContent:"space-between", alignItems:"center" }}>
          <span style={{ fontWeight:700, fontSize:15 }}>Configure Rule</span>
          <button onClick={onClose} style={{ background:"none", border:"none", fontSize:20, cursor:"pointer", color:"#6b7280" }}>×</button>
        </div>
        <div style={{ padding:18, display:"flex", flexDirection:"column", gap:12 }}>
          <div>
            <label style={S.label}>Rule Name</label>
            <input style={S.input} value={form.name||""} onChange={(e)=>setForm((f)=>({...f,name:e.target.value}))}/>
          </div>
          {action?.type === "send_whatsapp" && (
            <>
              <div>
                <label style={S.label}>Send WhatsApp to (phone)</label>
                <input style={S.input} placeholder="+91 98765 43210 (blank = customer phone)" value={action.params?.phone||""} onChange={(e)=>set("phone",e.target.value)}/>
              </div>
              <div>
                <label style={S.label}>Message Template</label>
                <textarea style={{ ...S.input, height:100, resize:"vertical" }} value={action.params?.message||""} onChange={(e)=>set("message",e.target.value)}/>
                <div style={{ fontSize:11, color:"#9ca3af", marginTop:3 }}>Variables: {"{{order_id}} {{customer}} {{total}} {{items}} {{product}} {{stock}}"}</div>
              </div>
            </>
          )}
          {action?.type === "webhook" && (
            <>
              <div>
                <label style={S.label}>Webhook URL</label>
                <input style={S.input} placeholder="https://your-system.com/webhook" value={action.params?.url||""} onChange={(e)=>set("url",e.target.value)}/>
              </div>
              <div>
                <label style={S.label}>Method</label>
                <select style={S.input} value={action.params?.method||"POST"} onChange={(e)=>set("method",e.target.value)}>
                  <option>POST</option><option>GET</option><option>PUT</option>
                </select>
              </div>
            </>
          )}
          {action?.type === "update_inventory" && (
            <div style={{ background:"#f0fdf4", borderRadius:8, padding:12, fontSize:12, color:"#15803d" }}>
              ✅ Automatically reduces Shopify stock by the exact quantity ordered. No extra configuration needed.
            </div>
          )}
          <div style={{ display:"flex", gap:10, justifyContent:"flex-end" }}>
            <button style={S.btnOut} onClick={onClose}>Cancel</button>
            <button style={S.btn()} onClick={async () => {
              try {
                if (form._id) {
                  await api("/automations/"+form._id, { method:"PUT", body:JSON.stringify(form) });
                } else {
                  await api("/automations", { method:"POST", body:JSON.stringify({ ...form, isActive:true }) });
                }
                toast("Automation saved ✓", "success");
                onSave(); onClose();
              } catch(e) { toast(e.message, "error"); }
            }}>Save & Enable</button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function PageAutomation({ toast }) {
  const [automations, setAutomations] = useState([]);
  const [loadError,   setLoadError]   = useState(null);
  const [loading,     setLoading]     = useState(true);
  const [tab,         setTab]         = useState("rules");
  const [logs,        setLogs]        = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [editAuto,    setEditAuto]    = useState(null);
  const [showModal,   setShowModal]   = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await api("/automations");
      setAutomations(data.automations || []);
    } catch(e) { console.error("Automations load error:", e.message); }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const loadLogs = async () => {
    setLogsLoading(true);
    try {
      const data = await api("/automations");
      const autos = data.automations || [];
      const allLogs = [];
      for (const a of autos.slice(0,5)) {
        try {
          const ld = await api("/automations/"+a._id+"/logs");
          (ld.logs||[]).forEach((l) => allLogs.push({ ...l, autoName:a.name }));
        } catch {}
      }
      allLogs.sort((a,b) => new Date(b.triggeredAt)-new Date(a.triggeredAt));
      setLogs(allLogs.slice(0,50));
    } catch(e) { toast(e.message, "error"); }
    setLogsLoading(false);
  };

  const toggle = async (id, current) => {
    try {
      await api("/automations/"+id+"/toggle", { method:"PUT" });
      setAutomations((prev) => prev.map((a) => a._id===id ? {...a, isActive:!current} : a));
      toast(current ? "Rule paused" : "Rule activated ✓", "success");
    } catch(e) { toast(e.message, "error"); }
  };

  const deleteAuto = async (id) => {
    if (!window.confirm("Delete this automation rule?")) return;
    try {
      await api("/automations/"+id, { method:"DELETE" });
      toast("Deleted ✓", "success");
      load();
    } catch(e) { toast(e.message, "error"); }
  };

  const addFromTemplate = (template) => {
    setEditAuto({ ...template, _id:null });
    setShowModal(true);
  };

  const activeCount = automations.filter((a) => a.isActive).length;

  if (loadError) return (
    <div style={{ padding:40, textAlign:"center" }}>
      <div style={{ fontSize:36, marginBottom:10 }}>⚡</div>
      <div style={{ fontWeight:600, marginBottom:8 }}>Automation page failed to load</div>
      <div style={{ fontSize:12, color:"#9ca3af", marginBottom:16 }}>{loadError}</div>
      <button style={{ padding:"8px 16px", borderRadius:7, background:"#F47920", color:"#fff", border:"none", cursor:"pointer" }} onClick={()=>{ setLoadError(null); load(); }}>↻ Retry</button>
    </div>
  );

  return (
    <div>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:14, flexWrap:"wrap", gap:10 }}>
        <div>
          <div style={{ fontWeight:700, fontSize:16 }}>⚡ Automation Rules</div>
          <div style={{ fontSize:12, color:"#9ca3af" }}>Rules run automatically in the background · {activeCount} active of {automations.length} rules</div>
        </div>
      </div>

      <div style={{ background:"linear-gradient(135deg,#1A3A5C,#2563eb)", borderRadius:10, padding:"14px 18px", marginBottom:14, color:"#fff", display:"flex", alignItems:"center", gap:16, flexWrap:"wrap" }}>
        <div style={{ flex:1 }}>
          <div style={{ fontWeight:700, fontSize:14, marginBottom:4 }}>⚡ How automations work</div>
          <div style={{ fontSize:12, opacity:.9 }}>Rules fire automatically when events happen. Toggle any rule ON and it runs silently in the background 24/7. You never need to visit this page again.</div>
        </div>
        <div style={{ display:"flex", gap:10, flexShrink:0, flexWrap:"wrap" }}>
          {[["🛍️ Event happens","New order arrives"],["⚡ Rule checks","Conditions match?"],["✅ Action runs","WhatsApp sent"]].map(([t,d])=>(
            <div key={t} style={{ background:"rgba(255,255,255,0.12)", borderRadius:8, padding:"8px 12px", textAlign:"center" }}>
              <div style={{ fontWeight:700, fontSize:12 }}>{t}</div>
              <div style={{ fontSize:10, opacity:.8 }}>{d}</div>
            </div>
          ))}
        </div>
      </div>

      <div style={{ background:"#fffbeb", borderRadius:8, border:"1px solid #fde68a", padding:"10px 14px", marginBottom:14, fontSize:12, color:"#92400e" }}>
        ⚠️ <strong>Register Shopify webhook</strong> for order automations to fire automatically:
        <code style={{ background:"#fef9c3", padding:"1px 6px", borderRadius:4, marginLeft:6, fontSize:11 }}>
          https://sellsync-server.onrender.com/api/webhooks/shopify/order
        </code>
        <span style={{ marginLeft:6 }}>→ Shopify Admin → Settings → Notifications → Webhooks → orders/create</span>
      </div>

      <div style={{ display:"flex", borderBottom:"1px solid #e5e7eb", marginBottom:16 }}>
        <button style={S.tab(tab==="rules")}     onClick={()=>setTab("rules")}>⚡ My Rules ({automations.length})</button>
        <button style={S.tab(tab==="templates")} onClick={()=>setTab("templates")}>📋 Add from Templates</button>
        <button style={S.tab(tab==="logs")}      onClick={()=>{ setTab("logs"); loadLogs(); }}>📋 Activity Log</button>
      </div>

      {tab === "rules" && (
        <div>
          {loading ? <div style={{ textAlign:"center", padding:40, color:"#9ca3af" }}>Loading…</div>
          : automations.length === 0 ? (
            <div style={{ ...S.card, padding:20 }}>
              <EmptyState
                icon="⚡"
                title="No automation rules yet"
                subtitle="Automations run on their own — alert you when stock runs low, add a packing task when an order comes in, and more. Set one up in 30 seconds."
                actionLabel="📋 Browse templates"
                onAction={()=>setTab("templates")}
                columns={["Trigger","Action","Status"]}
                exampleRows={[
                  ["Stock drops below 5","Send WhatsApp alert","Active"],
                  ["New order received","Create packing task","Active"],
                  ["Return marked","Notify accounts team","Paused"],
                ]}
              />
            </div>
          ) : (
            <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
              {automations.map((a) => {
                const trig = TRIGGER_LABELS[a.trigger?.type] || { label:a.trigger?.type||"", icon:"⚡", color:"#6b7280" };
                const acts = (a.actions||[]).map((ac) => ACTION_LABELS[ac.type] || { label:ac.type||"", icon:"•", color:"#6b7280" });
                return (
                  <div key={a._id} style={{ background:"#fff", borderRadius:10, border:"1px solid #e5e7eb", padding:"14px 16px", borderLeft:"4px solid "+(a.isActive?"#22c55e":"#d1d5db") }}>
                    <div style={{ display:"flex", alignItems:"center", gap:12, flexWrap:"wrap" }}>
                      <button style={S.toggle(a.isActive)} onClick={()=>toggle(a._id, a.isActive)}>
                        <div style={S.knob(a.isActive)}/>
                      </button>
                      <div style={{ flex:1, minWidth:0 }}>
                        <div style={{ display:"flex", alignItems:"center", gap:8, flexWrap:"wrap" }}>
                          <span style={{ fontWeight:700, fontSize:14 }}>{a.name}</span>
                          <span style={{ background:a.isActive?"#f0fdf4":"#f3f4f6", color:a.isActive?"#15803d":"#6b7280", fontSize:10, padding:"1px 8px", borderRadius:20, fontWeight:700 }}>
                            {a.isActive?"ACTIVE":"PAUSED"}
                          </span>
                        </div>
                        {a.description && <div style={{ fontSize:12, color:"#6b7280", marginTop:2 }}>{a.description}</div>}
                        <div style={{ display:"flex", alignItems:"center", gap:6, marginTop:8, flexWrap:"wrap" }}>
                          <span style={{ background:trig.color+"15", color:trig.color, padding:"2px 9px", borderRadius:10, fontSize:11, fontWeight:600 }}>{trig.icon} When: {trig.label}</span>
                          <span style={{ color:"#9ca3af" }}>→</span>
                          {acts.map((ac,i)=>(
                            <span key={i} style={{ background:"#f3f4f6", color:ac.color||"#6b7280", padding:"2px 9px", borderRadius:10, fontSize:11, fontWeight:600 }}>{ac.icon} {ac.label}</span>
                          ))}
                          {(a.conditions||[]).length > 0 && <span style={{ background:"#fffbeb", color:"#d97706", padding:"2px 9px", borderRadius:10, fontSize:11 }}>+ {a.conditions.length} condition{a.conditions.length!==1?"s":""}</span>}
                        </div>
                      </div>
                      <div style={{ display:"flex", gap:12, alignItems:"center", flexShrink:0 }}>
                        {a.runCount > 0 && <div style={{ textAlign:"center" }}><div style={{ fontSize:16, fontWeight:700, color:"#1A3A5C" }}>{a.runCount}</div><div style={{ fontSize:9, color:"#9ca3af" }}>runs</div></div>}
                        {a.lastRunAt && <div style={{ textAlign:"center" }}><div style={{ fontSize:10, color:a.lastStatus==="success"?"#15803d":a.lastStatus==="failed"?"#dc2626":"#6b7280", fontWeight:600 }}>{new Date(a.lastRunAt).toLocaleDateString("en-IN")}</div><div style={{ fontSize:9, color:"#9ca3af" }}>last run</div></div>}
                        <div style={{ display:"flex", gap:6 }}>
                          <button style={{ ...S.btnOut, fontSize:11, padding:"4px 10px" }} onClick={()=>{ setEditAuto(a); setShowModal(true); }}>Configure</button>
                          <button style={{ ...S.btnOut, fontSize:11, padding:"4px 10px", color:"#dc2626", borderColor:"#fecaca" }} onClick={()=>deleteAuto(a._id)}>Delete</button>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {tab === "templates" && (
        <div>
          <div style={{ fontSize:13, color:"#6b7280", marginBottom:14 }}>Click any template to configure and enable it. Once ON it runs automatically — no further action needed.</div>
          <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(300px,1fr))", gap:12 }}>
            {TEMPLATES.map((t, i) => {
              const alreadyAdded = automations.some((a) => a.name === t.name);
              const trig = TRIGGER_LABELS[t.trigger?.type] || { label:t.trigger?.type||"", icon:"⚡", color:"#6b7280" };
              const acts = t.actions.map((ac) => ACTION_LABELS[ac.type] || { label:ac.type, icon:"•", color:"#6b7280" });
              return (
                <div key={i} style={{ background:"#fff", borderRadius:10, border:"1px solid #e5e7eb", overflow:"hidden", borderTop:"3px solid "+t.color }}>
                  <div style={{ padding:"14px 16px" }}>
                    <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:6 }}>
                      <div style={{ fontWeight:700, fontSize:13 }}>{t.name}</div>
                      {t.popular && <span style={{ background:"#fff8f2", color:"#F47920", fontSize:9, padding:"1px 7px", borderRadius:20, fontWeight:700, flexShrink:0, marginLeft:6 }}>POPULAR</span>}
                    </div>
                    <div style={{ fontSize:12, color:"#6b7280", marginBottom:10 }}>{t.description}</div>
                    <div style={{ display:"flex", gap:6, flexWrap:"wrap", marginBottom:12 }}>
                      <span style={{ background:trig.color+"15", color:trig.color, padding:"2px 8px", borderRadius:10, fontSize:10, fontWeight:600 }}>{trig.icon} {trig.label}</span>
                      {acts.map((ac,j)=><span key={j} style={{ background:"#f3f4f6", color:ac.color||"#6b7280", padding:"2px 8px", borderRadius:10, fontSize:10, fontWeight:600 }}>{ac.icon} {ac.label}</span>)}
                    </div>
                    {alreadyAdded
                      ? <div style={{ background:"#f0fdf4", color:"#15803d", borderRadius:6, padding:"6px 10px", fontSize:12, fontWeight:600, textAlign:"center" }}>✅ Already added</div>
                      : <button style={{ ...S.btn(t.color), width:"100%", fontSize:12 }} onClick={()=>addFromTemplate(t)}>+ Add This Rule</button>
                    }
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {tab === "logs" && (
        <div style={S.card}>
          <div style={S.cardHdr}>
            <span>📋 Recent Automation Activity</span>
            <button style={{ ...S.btnOut, fontSize:11 }} onClick={loadLogs}>↻ Refresh</button>
          </div>
          {logsLoading ? <div style={{ textAlign:"center", padding:40, color:"#9ca3af" }}>Loading…</div>
          : logs.length === 0 ? (
            <div style={{ textAlign:"center", padding:40, color:"#9ca3af" }}>
              <div style={{ fontSize:36, marginBottom:10 }}>📋</div>
              No activity yet — rules will log here when they fire
            </div>
          ) : (
            <div style={{ overflowX:"auto" }}>
              <table style={{ width:"100%", borderCollapse:"collapse" }}>
                <thead><tr>
                  {["Time","Rule","Trigger","Status","Details"].map((h)=><th key={h} style={{ padding:"9px 12px", textAlign:"left", fontSize:11, fontWeight:600, color:"#6b7280", background:"#f9fafb" }}>{h}</th>)}
                </tr></thead>
                <tbody>
                  {logs.map((log, i) => (
                    <tr key={i} onMouseEnter={(e)=>e.currentTarget.style.background="#fafafa"} onMouseLeave={(e)=>e.currentTarget.style.background=""}>
                      <td style={{ padding:"8px 12px", borderBottom:"1px solid #f3f4f6", fontSize:11, color:"#6b7280", whiteSpace:"nowrap" }}>{new Date(log.triggeredAt).toLocaleString("en-IN",{ day:"2-digit", month:"short", hour:"2-digit", minute:"2-digit" })}</td>
                      <td style={{ padding:"8px 12px", borderBottom:"1px solid #f3f4f6", fontWeight:600, fontSize:12 }}>{log.autoName}</td>
                      <td style={{ padding:"8px 12px", borderBottom:"1px solid #f3f4f6", fontSize:11 }}>{TRIGGER_LABELS[log.trigger]?.icon} {TRIGGER_LABELS[log.trigger]?.label || log.trigger}</td>
                      <td style={{ padding:"8px 12px", borderBottom:"1px solid #f3f4f6" }}>
                        <span style={{ background:log.status==="success"?"#f0fdf4":log.status==="failed"?"#fef2f2":"#f3f4f6", color:log.status==="success"?"#15803d":log.status==="failed"?"#dc2626":"#6b7280", padding:"2px 8px", borderRadius:10, fontSize:10, fontWeight:700 }}>
                          {log.status==="success"?"✅ Success":log.status==="failed"?"❌ Failed":"⏭ Skipped"}
                        </span>
                      </td>
                      <td style={{ padding:"8px 12px", borderBottom:"1px solid #f3f4f6", fontSize:11, color:"#6b7280", maxWidth:280, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{log.message}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {showModal && editAuto && (
        <QuickEditModal auto={editAuto} onSave={load} onClose={()=>{ setShowModal(false); setEditAuto(null); }} toast={toast}/>
      )}
    </div>
  );
}
